import * as React from "react";
import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { RouteStatus, ConnectProto } from "@racoon/api";
import { isShiftRunning, isShiftTerminal, isTtnApproved } from "~/shared/lib/shift-status";
import { IcCar, IcFilter } from "@racoon/icons";
import { Namespace, useT, useTypedT } from "@racoon/i18n";
import { toTimestamp, normalizeForSearch } from "@racoon/lib";
import {
  Button,
  Checkbox,
  Column,
  DataTable,
  DatePicker,
  Hyperlink,
  IconButton,
  SearchablePopover,
  SearchableSelect,
  type SearchableSelectOption,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
  useToast,
} from "@racoon/ui";
import { humanTruckType } from "~/pages/dashboard/autopark/helpers";
import { ShiftStatusBadge } from "~/pages/dashboard/autopark/components/ShiftStatusBadge";
import { useListTruckDriverOnShiftsQuery } from "~/shared/api/shift/driver-on-shift.grpc";
import {
  useChangeRouteDriverOnShiftMutation,
  useCreateRouteWithJobsMutation,
  useListRoutesQuery,
  useMoveJobsBetweenRoutesMutation,
  useUpdateRouteWithJobsMutation,
} from "~/shared/api/routes/route.grpc";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import {
  areJobsCompatibleWithRouteCategory,
  isSingleJobPerTripTruck,
  areJobsServableByTruck,
  getRouteCategoriesForJobCategory,
  isTruckTypeCompatibleWithRouteCategory,
} from "../../hooks/msw-route-list/jobCategoryRouteCompatibility";
import { getUtcDayRange } from "../../utils/dayRange";
import { getRouteJobsCount, getRouteTotalVolume } from "../../utils/routeMetrics";
import { tripLike } from "~/shared/lib/trip-like";
import { useRouteJobsModalOutsideRouter } from "../../hooks/useRouteJobsModal";
import { RouteStatusBadge } from "../RouteStatusBadge";
import { compareIds } from "~/shared/lib/compare-ids";
import { shiftNumber } from "~/shared/lib/shift-number";
import { ModalHeading } from "~/shared/ui/modal-heading";
import { Notice } from "~/shared/ui/notice";
import { CreateShiftModal } from "~/pages/dashboard/autopark/components/CreateShiftModal";
import { useReissueExpiredJobs } from "~/shared/hooks/use-reissue-expired-jobs";
import { useActiveDriverOptions } from "~/shared/api/user/use-active-driver-options";
import {
  canReceiveTransfer,
  transferPlan,
  transferableJobs,
  type TransferItem,
} from "~/features/force-complete-shift/lib/force-complete-shift";

/**
 * Routes that may still take work — a finished or cancelled one is not a target.
 * NOT_APPROVED belongs here: on the MSW page routes live in that state until the
 * dispatcher approves the shift, and they are the usual destination.
 */
const ASSIGNABLE_TARGET_STATUSES = [
  ConnectProto.RouteStatus.NOT_APPROVED,
  ConnectProto.RouteStatus.CREATED,
  ConnectProto.RouteStatus.IN_PROGRESS,
  ConnectProto.RouteStatus.RETURNING,
];

/** Id of the placeholder row that stands for "a trip that does not exist yet". */
const NEW_ROUTE_ID = -1n;

/** What the backend lets a truck that hauls containers one at a time take per trip. */
const TWO_JOB_LIMIT = 2;

/**
 * "jobs"       — move the picked jobs of a route onto another route (Figma 20563:33839).
 * "route"      — move the whole route onto another vehicle's shift (Figma 20563:34418).
 * "unassigned" — put jobs that are on no route yet onto an existing trip; the
 *                entry point from the orders table's "Действие" menu.
 * "shift"      — pick the shift that takes every unfinished route and job of a shift
 *                being force-completed (Figma 21279:43447). Its own trips are shown
 *                for context only; below them, what the transfer will put there.
 *                The parent sends the request; this only reports the pick through
 *                onTargetChange.
 */
export type RouteTransferMode = "jobs" | "route" | "unassigned" | "shift";

export type RouteTransferModalContentProps = {
  /** Defaults to moving jobs — the entry point most call sites use. */
  mode?: RouteTransferMode;
  /** Route the jobs travel from; there is none in "unassigned" mode. */
  sourceRoute?: ConnectProto.Route;
  /** Jobs to move; ignored in "route" mode, where the whole route travels. */
  sourceSelectedJobIds?: bigint[];
  /**
   * Jobs to add in "unassigned" mode. They carry no route, so the job objects
   * themselves are the only way to tell what a target trip may accept.
   */
  unassignedJobs?: ConnectProto.Job[];
  /** "shift" mode: the shift being ended, never a target, and its truck, the starting point. */
  sourceShiftId?: bigint;
  sourceTruckId?: bigint;
  /** "shift" mode: the unfinished routes of the shift; transferPlan says what travels. */
  sourceRoutes?: ConnectProto.Route[];
  /** "shift" mode: the shift picked, as it changes; null once there is none. */
  onTargetChange?: (target: bigint | null) => void;
  initialDate?: Date;
  onClose: () => void;
  onSuccess?: (sourceRouteId?: bigint) => void;
};

const NO_JOBS: ConnectProto.Job[] = [];
const NO_ROUTES: ConnectProto.Route[] = [];

/** A preview row stands for the source route and carries only the jobs that travel. */
type TargetRoute = ConnectProto.Route & { isVirtual?: boolean; preview?: TransferItem["kind"] };

type VehicleGroup = {
  truck: ConnectProto.Truck;
  /** The shifts on offer, numbered against allShifts as shiftNumber asks. */
  shifts: ConnectProto.DriverOnShift[];
  allShifts: ConnectProto.DriverOnShift[];
};

const formatDriverName = (
  shift: ConnectProto.DriverOnShift | undefined,
  fallbackName: string,
): string => {
  const fullName = [shift?.driver?.firstName, shift?.driver?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || fallbackName;
};

const getTruckTypeValue = (truck: ConnectProto.Truck | undefined) =>
  truck?.truckType?.type ?? truck?.type;

const activeShiftDriverName = (shifts: ConnectProto.DriverOnShift[]): string =>
  formatDriverName(
    shifts.find((shift) => isShiftRunning(shift.status)),
    "",
  );

export const RouteTransferModalContent: React.FC<RouteTransferModalContentProps> = ({
  mode = "jobs",
  sourceRoute,
  sourceSelectedJobIds = [],
  unassignedJobs = NO_JOBS,
  sourceShiftId: sourceShiftIdProp,
  sourceTruckId: sourceTruckIdProp,
  sourceRoutes = NO_ROUTES,
  onTargetChange,
  initialDate,
  onClose,
  onSuccess,
}) => {
  const t = useTypedT(Namespace.Routes);
  const tForce = useTypedT(Namespace.ForceCompleteShift);
  const tAny = useT();
  const { toast } = useToast();
  const { selectedCompanyId } = useSelectedCompany();
  const ownerOrganizationId = BigInt(selectedCompanyId ?? 1);

  const isRouteMode = mode === "route";
  const isUnassignedMode = mode === "unassigned";
  const isShiftMode = mode === "shift";
  const { openRouteJobs } = useRouteJobsModalOutsideRouter();

  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    initialDate ?? new Date(),
  );
  const [truckTypeFilter, setTruckTypeFilter] = React.useState("");
  const [subOrgFilter, setSubOrgFilter] = React.useState("");
  // The jobs being placed decide what the target must be able to do, so
  // respecting them is the default; unchecking widens the list to every vehicle
  // and trip.
  const [respectCapabilities, setRespectCapabilities] = React.useState(true);
  const [selectedTruckId, setSelectedTruckId] = React.useState<bigint | null>(null);
  const [activeShiftId, setActiveShiftId] = React.useState<bigint | null>(null);
  const [targetRouteId, setTargetRouteId] = React.useState<bigint | null>(null);
  // "Create a new trip" only adds the placeholder row for the shift it was
  // pressed on — in route mode the placeholder is the only possible target.
  const [newTripShiftId, setNewTripShiftId] = React.useState<bigint | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [moveJobsBetweenRoutes] = useMoveJobsBetweenRoutesMutation();
  const [createRouteWithJobs] = useCreateRouteWithJobsMutation();
  const [updateRouteWithJobs] = useUpdateRouteWithJobsMutation();
  const [changeRouteDriverOnShift] = useChangeRouteDriverOnShiftMutation();
  const { reissueExpiredJobs } = useReissueExpiredJobs();

  const sourceShiftId = sourceShiftIdProp ?? sourceRoute?.driverOnShift?.id;
  const sourceTruckId =
    sourceTruckIdProp ??
    sourceRoute?.driverOnShift?.truck?.id ??
    sourceRoute?.driverOnShift?.truckId;

  const selectedJobs = React.useMemo(() => {
    if (isShiftMode) return transferableJobs(sourceRoutes);
    if (isUnassignedMode) return unassignedJobs;
    if (isRouteMode) return sourceRoute?.jobs ?? [];
    const selectedIds = new Set(sourceSelectedJobIds);
    return (sourceRoute?.jobs ?? []).filter((job) => selectedIds.has(job.id));
  }, [
    isShiftMode,
    sourceRoutes,
    isUnassignedMode,
    unassignedJobs,
    isRouteMode,
    sourceRoute?.jobs,
    sourceSelectedJobIds,
  ]);

  // The category the target has to be able to serve. In the transfer modes the
  // source route states it outright; with unassigned jobs it is implied by what
  // they are. A mixed selection leaves it unset, so nothing is hidden on a guess
  // — the backend still refuses a genuinely invalid target.
  const targetRouteCategory = React.useMemo(() => {
    if (!isUnassignedMode && !isShiftMode) return sourceRoute?.routeCategory;

    const jobCategories = new Set(
      selectedJobs
        .map((job) => job.jobCategory)
        .filter(
          (category): category is ConnectProto.JobCategory =>
            category != null && category !== ConnectProto.JobCategory.UNDEFINED,
        ),
    );
    if (jobCategories.size !== 1) return undefined;

    return getRouteCategoriesForJobCategory([...jobCategories][0])[0];
  }, [isUnassignedMode, isShiftMode, sourceRoute?.routeCategory, selectedJobs]);

  const dayRange = React.useMemo(() => getUtcDayRange(selectedDate), [selectedDate]);

  const { data: shiftsResponse, isLoading: isLoadingShifts } = useListTruckDriverOnShiftsQuery(
    {
      ownerOrganizationId,
      datetimeFilter: {
        from: toTimestamp(new Date(dayRange.start)),
        to: toTimestamp(new Date(dayRange.end)),
      },
    },
    { skip: !selectedCompanyId || !selectedDate },
  );

  // Shift mode never offers the ending shift's own truck, nor a shift that cannot take
  // the jobs, and keeps a truck with nothing eligible yet: "Запланировать новую смену"
  // can give it a shift.
  const allVehicles = React.useMemo<VehicleGroup[]>(() => {
    return (shiftsResponse?.truckDriverOnShifts ?? [])
      .filter(
        (group): group is ConnectProto.TruckDriverOnShift & { truck: ConnectProto.Truck } =>
          group.truck?.id !== undefined && BigInt(group.truck.id) !== 0n,
      )
      .map((group) => ({
        truck: group.truck,
        // The backend lists a day's shifts newest first; the tabs count up from "Смена 1".
        shifts: (group.driverOnShifts ?? [])
          .filter(
            (shift) =>
              !isShiftMode || sourceShiftId == null || canReceiveTransfer(shift, sourceShiftId),
          )
          .sort((a, b) => compareIds(a.id, b.id)),
        allShifts: group.driverOnShifts ?? [],
      }))
      .filter((group) =>
        isShiftMode
          ? sourceTruckId == null || BigInt(group.truck.id) !== BigInt(sourceTruckId)
          : group.shifts.length > 0,
      );
  }, [shiftsResponse, isShiftMode, sourceShiftId, sourceTruckId]);

  // Drivers and trucks already on a shift that day are not offered for a new one.
  const activeShifts = React.useMemo(
    () =>
      (shiftsResponse?.truckDriverOnShifts ?? []).flatMap((group) =>
        (group.driverOnShifts ?? [])
          .filter((shift) => !isShiftTerminal(shift.status))
          .map((shift) => ({
            driver_id: Number(shift.driverId ?? 0),
            truck_id: Number(group.truck?.id ?? 0),
          })),
      ),
    [shiftsResponse],
  );

  const truckTypeOptions = React.useMemo(() => {
    const options = new Map<string, string>();
    allVehicles.forEach(({ truck }) => {
      const type = getTruckTypeValue(truck);
      if (type != null && type !== ConnectProto.TruckType.UNDEFINED) {
        options.set(String(type), humanTruckType(type, tAny));
      }
    });
    return Array.from(options, ([id, name]): SearchableSelectOption => ({ id, name }));
  }, [allVehicles, tAny]);

  const subOrgOptions = React.useMemo(() => {
    const names = new Set<string>();
    allVehicles.forEach(({ truck }) => {
      if (truck.ownerOrganization?.name) names.add(truck.ownerOrganization.name);
    });
    return Array.from(names, (name): SearchableSelectOption => ({ id: name, name }));
  }, [allVehicles]);

  // Only the vehicles that can actually do the jobs being placed: the truck type
  // has to serve their category, and its capability pairs have to cover each
  // job's (job type, container type). The route-category check stays alongside
  // it — in "route" mode the source route states its category even when its job
  // list is empty.
  const vehicles = React.useMemo(() => {
    return allVehicles.filter(({ truck }) => {
      if (truckTypeFilter && String(getTruckTypeValue(truck)) !== truckTypeFilter) return false;
      if (subOrgFilter && truck.ownerOrganization?.name !== subOrgFilter) return false;
      if (!respectCapabilities) return true;
      if (!isTruckTypeCompatibleWithRouteCategory(getTruckTypeValue(truck), targetRouteCategory)) {
        return false;
      }
      return areJobsServableByTruck(selectedJobs, truck);
    });
  }, [
    allVehicles,
    truckTypeFilter,
    subOrgFilter,
    respectCapabilities,
    targetRouteCategory,
    selectedJobs,
  ]);

  // The vehicle the route came from is the natural starting point; when the
  // filters drop it (or it is not on shift today) fall back to the first one.
  React.useEffect(() => {
    if (vehicles.length === 0) {
      setSelectedTruckId(null);
      return;
    }
    setSelectedTruckId((current) => {
      if (current != null && vehicles.some((v) => BigInt(v.truck.id) === current)) return current;
      const sourceVehicle =
        sourceTruckId != null
          ? vehicles.find((v) => BigInt(v.truck.id) === BigInt(sourceTruckId))
          : undefined;
      return BigInt((sourceVehicle ?? vehicles[0]).truck.id);
    });
  }, [vehicles, sourceTruckId]);

  const selectedVehicleIndex = React.useMemo(
    () => vehicles.findIndex((v) => BigInt(v.truck.id) === selectedTruckId),
    [vehicles, selectedTruckId],
  );
  const selectedVehicle = selectedVehicleIndex >= 0 ? vehicles[selectedVehicleIndex] : undefined;
  const selectedTruck = selectedVehicle?.truck;
  const shifts = React.useMemo(() => selectedVehicle?.shifts ?? [], [selectedVehicle]);

  const shiftIds = React.useMemo(() => shifts.map((shift) => BigInt(shift.id)), [shifts]);

  const { data: routesResponse, isLoading: isLoadingRoutes } = useListRoutesQuery(
    {
      ownerOrganizationIds: selectedCompanyId ? [BigInt(selectedCompanyId)] : [],
      driverOnShiftIds: shiftIds,
    },
    { skip: shiftIds.length === 0 },
  );

  const routesByShiftId = React.useMemo(() => {
    const map = new Map<bigint, ConnectProto.Route[]>();
    (routesResponse?.routes ?? []).forEach((route) => {
      const shiftId = route.driverOnShift?.id;
      if (shiftId === undefined) return;
      const list = map.get(BigInt(shiftId)) ?? [];
      list.push(route);
      map.set(BigInt(shiftId), list);
    });
    return map;
  }, [routesResponse]);

  // Prefer the shift the route sits on while the source vehicle is shown, so
  // the operator lands on the context they came from.
  React.useEffect(() => {
    if (shifts.length === 0) {
      setActiveShiftId(null);
      return;
    }
    setActiveShiftId((current) => {
      if (current != null && shifts.some((shift) => BigInt(shift.id) === current)) return current;
      const sourceShift =
        sourceShiftId != null
          ? shifts.find((shift) => BigInt(shift.id) === BigInt(sourceShiftId))
          : undefined;
      // The tabs run oldest first, so the first one may be over; land on an open shift.
      const open = shifts.find((shift) => !isShiftTerminal(shift.status));
      return BigInt((sourceShift ?? open ?? shifts[shifts.length - 1]).id);
    });
  }, [shifts, sourceShiftId]);

  const activeShift = React.useMemo(
    () => shifts.find((shift) => BigInt(shift.id) === activeShiftId),
    [shifts, activeShiftId],
  );

  // Switching vehicle or shift invalidates whatever was picked before.
  React.useEffect(() => {
    setTargetRouteId(null);
    setNewTripShiftId(null);
  }, [selectedTruckId, activeShiftId]);

  const isSourceShift =
    activeShiftId != null && sourceShiftId != null && activeShiftId === BigInt(sourceShiftId);
  // Shift mode: a truck with no shift yet shows only "Запланировать новую смену".
  const hideTargets = isShiftMode && selectedTruck != null && shifts.length === 0;

  const preview = React.useMemo(() => transferPlan(sourceRoutes), [sourceRoutes]);

  // Shift mode: the backend lets a hook-lift or multilift truck carry two jobs a trip,
  // and would refuse a bigger trip only after its jobs were detached. Such a truck is
  // not a target for those trips, and the reason is shown under the table.
  const overfull = React.useMemo(
    () =>
      isShiftMode && isSingleJobPerTripTruck(selectedTruck)
        ? preview.filter((item) => item.jobs.length > TWO_JOB_LIMIT)
        : [],
    [isShiftMode, selectedTruck, preview],
  );

  // Shift mode: the active shift is the target, and the parent sends the request,
  // so it hears the pick as it changes.
  React.useEffect(() => {
    if (!isShiftMode || !onTargetChange) return;
    onTargetChange(overfull.length > 0 ? null : activeShiftId);
  }, [isShiftMode, onTargetChange, activeShiftId, overfull]);

  const rows = React.useMemo<TargetRoute[]>(() => {
    const shiftRoutes = activeShiftId != null ? (routesByShiftId.get(activeShiftId) ?? []) : [];

    // Route mode keeps every trip on screen for context — the target is the
    // shift itself, so the rows are informational and only the placeholder is
    // selectable. Jobs mode narrows the list down to viable targets. Shift mode
    // shows the shift's open trips for context, nothing lands on them.
    const visible = isRouteMode
      ? shiftRoutes
      : shiftRoutes
          .filter((route) => sourceRoute == null || route.id !== sourceRoute.id)
          .filter((route) => ASSIGNABLE_TARGET_STATUSES.includes(route.status))
          .filter(
            (route) =>
              isShiftMode ||
              !respectCapabilities ||
              areJobsCompatibleWithRouteCategory(selectedJobs, route.routeCategory),
          );

    // Shift mode: below the shift's trips, what the transfer puts there — the design's blue rows.
    if (isShiftMode) {
      if (activeShiftId == null) return visible;
      return [
        ...visible,
        ...preview.map((item) => ({ ...item.route, jobs: item.jobs, preview: item.kind })),
      ];
    }

    // Unassigned jobs have no source route to copy a departure point and an
    // unloading site from, so a brand-new trip cannot be built here — that is
    // what "Создать новый маршрут" (assign-route) in the orders menu is for.
    const showNewTrip = isRouteMode
      ? !isSourceShift && activeShiftId != null
      : !isUnassignedMode && activeShiftId != null && newTripShiftId === activeShiftId;

    if (!showNewTrip || !sourceRoute) return visible;

    return [
      ...visible,
      {
        ...sourceRoute,
        id: NEW_ROUTE_ID,
        isVirtual: true,
        jobs: [],
        status: ConnectProto.RouteStatus.UNDEFINED,
      } as TargetRoute,
    ];
  }, [
    activeShiftId,
    routesByShiftId,
    isRouteMode,
    isUnassignedMode,
    isShiftMode,
    isSourceShift,
    newTripShiftId,
    preview,
    respectCapabilities,
    selectedJobs,
    sourceRoute,
  ]);

  const columns = React.useMemo<Column<TargetRoute>[]>(() => {
    const select: Column<TargetRoute> = {
      id: "select",
      header: () => null,
      accessor: () => null,
      width: 64,
      sortable: false,
      cell: ({ row }) => {
        // In route mode an existing trip is not a target — the route lands on
        // the shift as a new trip, so only the placeholder carries a radio.
        if (isRouteMode && !row.original.isVirtual) return null;
        return (
          <div className="flex justify-center">
            <input
              type="radio"
              name="route-transfer-target"
              data-testid={`route-transfer-target-${row.original.isVirtual ? "new" : String(row.original.id)}`}
              className="size-4 cursor-pointer accent-blue-600"
              checked={targetRouteId === row.original.id}
              onChange={() => setTargetRouteId(row.original.id)}
            />
          </div>
        );
      },
    };
    return [
      // Shift mode picks a shift, not a row.
      ...(isShiftMode ? [] : [select]),
      {
        id: "trip",
        header: t("trip"),
        accessor: (row) => Number(row.id),
        width: isShiftMode ? 72 : 80,
        sortable: false,
        cell: ({ row }) => <div className="text-center">{row.index + 1}</div>,
      },
      {
        id: "routeId",
        header: t("colRouteId"),
        accessor: (row) => (row.isVirtual ? t("transferHere") : `M${row.id}`),
        // Shift mode has no "Перенести сюда" link to fit, so the column is as narrow as the design's.
        width: isShiftMode ? 120 : 162,
        sortable: false,
        cell: ({ row }) =>
          row.original.isVirtual ? (
            <Hyperlink
              className="text-sm font-medium"
              onOpen={() => setTargetRouteId(NEW_ROUTE_ID)}
            >
              {t("transferHere")}
            </Hyperlink>
          ) : (
            <Hyperlink className="text-sm" onOpen={() => openRouteJobs(row.original)}>
              M{String(row.original.id)}
            </Hyperlink>
          ),
      },
      {
        id: "status",
        header: t("colStatus"),
        accessor: () => null,
        // The table gives an unsized column 150px; the preview sentence needs the design's width on one line.
        width: isShiftMode ? 568 : undefined,
        sortable: false,
        cell: ({ row }) =>
          row.original.preview ? (
            <span
              data-testid={`force-complete-shift-preview-${String(row.original.id)}`}
              data-kind={row.original.preview}
            >
              {tForce(
                row.original.preview === "move"
                  ? "transfer.willMoveRoute"
                  : "transfer.willCreateRoute",
              )}
            </span>
          ) : row.original.isVirtual ? null : (
            <RouteStatusBadge
              status={
                ConnectProto.RouteStatusSchema.values.find(
                  (value) => value.number === Number(row.original.status || 0),
                )?.name as RouteStatus
              }
              jobs={row.original.jobs as any}
            />
          ),
      },
      {
        id: "jobsCount",
        header: t("colJobsCount"),
        accessor: (row) => (row.isVirtual ? 0 : getRouteJobsCount(row)),
        width: 160,
        sortable: false,
        cell: ({ row }) => (
          <div className="text-center">
            {row.original.isVirtual ? null : getRouteJobsCount(row.original)}
          </div>
        ),
      },
      {
        id: "volume",
        header: t("colTotalVolume"),
        accessor: (row) => (row.isVirtual ? 0 : getRouteTotalVolume(row)),
        width: 140,
        sortable: false,
        // Container volumes go down to 0.24 m³, so two decimals is the coarsest
        // rounding that still keeps small groups distinguishable.
        cell: ({ row }) => (
          <div className="text-center">
            {row.original.isVirtual
              ? null
              : `${Math.round(getRouteTotalVolume(row.original) * 100) / 100} ${t("volumeUnit")}`}
          </div>
        ),
      },
    ];
  }, [t, tForce, isRouteMode, isShiftMode, targetRouteId, openRouteJobs]);

  const canSubmit =
    targetRouteId != null &&
    !isSubmitting &&
    (isRouteMode ? activeShiftId != null && !isSourceShift : true);

  const handleConfirm = React.useCallback(async () => {
    if (targetRouteId == null) {
      toast({
        title: t("error"),
        description: t("transferModalChooseTarget"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (isUnassignedMode) {
        // A Невывоз goes on the trip as a re-issued attempt at its order, once the
        // dispatcher agrees; declining stops here.
        const routeIdOf = await reissueExpiredJobs(selectedJobs);
        if (!routeIdOf) return;

        // UpdateRouteWithJobs replaces the whole job list, so the jobs already
        // on the target trip have to travel along with the added ones.
        const targetRoute = rows.find((row) => row.id === targetRouteId);
        const jobIds = Array.from(
          new Set([
            ...(targetRoute?.jobs ?? []).map((job) => BigInt(job.id)),
            ...selectedJobs.map((job) => routeIdOf(job.id)),
          ]),
        );

        await updateRouteWithJobs({
          route: { id: targetRouteId },
          jobIds,
          ownerOrganizationId,
        }).unwrap();

        toast({ title: t("success"), description: t("jobsAddedSuccess") });
      } else if (isRouteMode) {
        if (activeShiftId == null) return;
        if (!sourceRoute) return;

        await changeRouteDriverOnShift({
          routeId: sourceRoute.id,
          driverOnShiftId: activeShiftId,
          ownerOrganizationId,
        }).unwrap();

        toast({ title: t("success"), description: t("transferRouteSuccess") });
      } else {
        if (!sourceRoute) return;

        const jobIds = Array.from(new Set(sourceSelectedJobIds));
        if (jobIds.length === 0) {
          toast({
            title: t("error"),
            description: t("transferModalChooseJobs"),
            variant: "destructive",
          });
          return;
        }

        let destinationRouteId = targetRouteId;

        if (destinationRouteId === NEW_ROUTE_ID) {
          const newRoute = await createRouteWithJobs({
            create: {
              ...tripLike(sourceRoute, activeShiftId!, ownerOrganizationId),
              planStartAt: selectedDate
                ? { ...toTimestamp(selectedDate), $typeName: "google.protobuf.Timestamp" as const }
                : undefined,
              planEndAt: sourceRoute.planEndAt
                ? {
                    seconds: sourceRoute.planEndAt.seconds,
                    nanos: sourceRoute.planEndAt.nanos,
                    $typeName: "google.protobuf.Timestamp" as const,
                  }
                : undefined,
            },
            ownerOrganizationId,
            jobIds: [],
          }).unwrap();

          destinationRouteId = BigInt(newRoute.id);
        }

        await moveJobsBetweenRoutes({
          sourceRouteId: sourceRoute.id,
          destinationRouteId,
          jobIds,
          ownerOrganizationId,
        }).unwrap();

        const sourceEmptied =
          sourceRoute.status !== ConnectProto.RouteStatus.CREATED &&
          sourceRoute.status !== ConnectProto.RouteStatus.NOT_APPROVED &&
          (sourceRoute.jobs ?? []).every((job) => jobIds.includes(job.id));

        toast({
          title: t("success"),
          description: sourceEmptied ? t("routeCancelledAsEmpty") : t("transferModalSuccess"),
        });
      }

      onSuccess?.(sourceRoute?.id);
      onClose();
    } catch (error) {
      console.error("Route transfer failed", error);
      toast({
        title: t("error"),
        description: isUnassignedMode
          ? t("jobsAddError")
          : isRouteMode
            ? t("transferRouteFailed")
            : t("transferModalSourceUpdateFailed"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    targetRouteId,
    isRouteMode,
    isUnassignedMode,
    activeShiftId,
    changeRouteDriverOnShift,
    createRouteWithJobs,
    moveJobsBetweenRoutes,
    updateRouteWithJobs,
    reissueExpiredJobs,
    onClose,
    onSuccess,
    ownerOrganizationId,
    rows,
    selectedDate,
    selectedJobs,
    sourceRoute,
    sourceSelectedJobIds,
    t,
    toast,
  ]);

  const stepVehicle = (delta: number) => {
    if (vehicles.length === 0) return;
    const nextIndex = (selectedVehicleIndex + delta + vehicles.length) % vehicles.length;
    setSelectedTruckId(BigInt(vehicles[nextIndex].truck.id));
  };

  const confirmLabel = isRouteMode
    ? t("transferModalConfirmRoute")
    : isUnassignedMode
      ? t("addJobsConfirmN", { count: selectedJobs.length })
      : t("transferModalConfirmJobs", { count: sourceSelectedJobIds.length });

  const title = isRouteMode
    ? t("transferRouteModalTitle")
    : isUnassignedMode
      ? t("addJobsToRoute")
      : t("transferModalTitle");

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white">
      {!isShiftMode && (
        <>
          <header className="flex shrink-0 items-center gap-4 p-4 pr-12">
            <ModalHeading>{title}</ModalHeading>
          </header>

          <Separator />
        </>
      )}

      {/* Filters + vehicle picker */}
      <div className="flex shrink-0 flex-col gap-6 p-6">
        <div className="flex flex-wrap items-center gap-4">
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            className="h-8 shrink-0 justify-start rounded-lg border border-slate-200 px-3 text-xs"
          />

          <div className="flex min-w-0 flex-1 items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-2">
              <TransferFilters
                truckTypeValue={truckTypeFilter}
                truckTypeOptions={truckTypeOptions}
                onTruckTypeChange={setTruckTypeFilter}
                subOrgValue={subOrgFilter}
                subOrgOptions={subOrgOptions}
                onSubOrgChange={setSubOrgFilter}
              />

              {truckTypeFilter && (
                <FilterChip
                  label={
                    truckTypeOptions.find((option) => option.id === truckTypeFilter)?.name ??
                    truckTypeFilter
                  }
                  onRemove={() => setTruckTypeFilter("")}
                />
              )}
              {subOrgFilter && (
                <FilterChip label={subOrgFilter} onRemove={() => setSubOrgFilter("")} />
              )}
            </div>

            <label className="flex shrink-0 cursor-pointer items-center gap-2 text-sm font-medium text-text-body">
              <Checkbox
                checked={respectCapabilities}
                onCheckedChange={(checked) => setRespectCapabilities(checked === true)}
              />
              {isShiftMode ? t("respectTruckCapabilities") : t("respectJobRequirements")}
            </label>
          </div>
        </div>

        <div className="flex items-end justify-between gap-4">
          <div className="flex w-[398px] max-w-full flex-col gap-2">
            <span className="text-xs font-medium tracking-[-0.01px] text-[#667489]">
              {isShiftMode
                ? tForce("transfer.selectTruckHint")
                : isUnassignedMode
                  ? t("addJobsSelectTruckHint")
                  : t("transferSelectTruckHint")}
            </span>

            <div className="flex items-start gap-2">
              <div className="flex items-center gap-1">
                <IconButton
                  size="lg"
                  className="rounded-lg border border-slate-200"
                  aria-label={t("nextTruck")}
                  disabled={vehicles.length < 2}
                  onClick={() => stepVehicle(1)}
                >
                  <ChevronDown />
                </IconButton>
                <IconButton
                  size="lg"
                  className="rounded-lg border border-slate-200"
                  aria-label={t("previousTruck")}
                  disabled={vehicles.length < 2}
                  onClick={() => stepVehicle(-1)}
                >
                  <ChevronUp />
                </IconButton>
              </div>

              <VehiclePicker
                vehicles={vehicles}
                selectedTruckId={selectedTruckId}
                onSelect={setSelectedTruckId}
              />
            </div>
          </div>
          {isShiftMode && selectedTruck && (
            <PlanShiftButton
              truck={selectedTruck}
              date={selectedDate}
              activeShifts={activeShifts}
              onCreated={(created) => created && setActiveShiftId(BigInt(created.id))}
            />
          )}
        </div>
      </div>

      <Separator className={cn(hideTargets && "hidden")} />

      {/* Target shift + trips */}
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-6",
          hideTargets && "hidden",
        )}
      >
        {isLoadingShifts && <Notice>{t("loadingShifts")}</Notice>}

        {!isLoadingShifts && !selectedTruck && (
          <Notice>
            {vehicles.length === 0 && allVehicles.length > 0
              ? t("trucksNotFound")
              : t("transferModalNoShifts")}
          </Notice>
        )}

        {selectedTruck && !hideTargets && (
          <>
            {!isShiftMode && (
              <div className="flex flex-wrap items-center gap-4 rounded-xl bg-slate-100 p-4">
                <span className="rounded-[4px] border border-text-body bg-white p-1 text-base font-semibold leading-4 tracking-[-0.01px] text-text-body">
                  {selectedTruck.plateNumber ?? "—"}
                </span>
                <div className="flex flex-wrap items-center gap-4 text-sm text-[#667489]">
                  <span>{selectedTruck.truckType?.name ?? selectedTruck.model ?? "—"}</span>
                  <span>{humanTruckType(getTruckTypeValue(selectedTruck), tAny)}</span>
                  <span>{selectedTruck.ownerOrganization?.name ?? "—"}</span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <Tabs
                  value={activeShiftId != null ? String(activeShiftId) : ""}
                  onValueChange={(value) => setActiveShiftId(BigInt(value))}
                >
                  <TabsList>
                    {shifts.map((shift) => (
                      <TabsTrigger
                        key={String(shift.id)}
                        value={String(shift.id)}
                        className="gap-2"
                      >
                        {isShiftRunning(shift.status) && (
                          <span className="size-2 rounded-full bg-icon-success" />
                        )}
                        <span>
                          {t("shiftIndex", {
                            index: shiftNumber(shift, selectedVehicle?.allShifts ?? shifts),
                          })}
                        </span>
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>

                {activeShift && (
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-text-body">
                      {formatDriverName(activeShift, t("driverNotAssigned"))}
                    </span>
                    <ShiftStatusBadge
                      status={activeShift.status || 0}
                      ttnApproved={isTtnApproved(activeShift)}
                    />
                  </div>
                )}
              </div>

              {isRouteMode && isSourceShift && (
                <p className="text-xs text-[#667489]">{t("transferRouteSameShift")}</p>
              )}

              <DataTable
                columns={columns}
                data={rows}
                loading={isLoadingRoutes}
                zebra={false}
                rowKey={(row) => String(row.id)}
                rowClassName={(row) => (row.preview ? "text-text-information" : "")}
                onRowClick={
                  isShiftMode
                    ? undefined
                    : (row) => {
                        if (isRouteMode && !row.isVirtual) return;
                        setTargetRouteId(row.id);
                      }
                }
                namespace={Namespace.Routes}
                empty={t("transferModalNoTargetRoutes")}
              />

              {overfull.length > 0 && (
                <Notice>
                  {tForce("transfer.overTruckLimit", {
                    limit: TWO_JOB_LIMIT,
                    trips: overfull.map((item) => `M${String(item.route.id)}`).join(", "),
                  })}
                </Notice>
              )}

              {!isRouteMode &&
                !isUnassignedMode &&
                !isShiftMode &&
                activeShiftId != null &&
                newTripShiftId !== activeShiftId && (
                  <Button
                    variant="ghost"
                    className="h-8 w-fit gap-1 px-3 text-sm font-medium text-blue-600 hover:text-blue-700"
                    onClick={() => {
                      setNewTripShiftId(activeShiftId);
                      setTargetRouteId(NEW_ROUTE_ID);
                    }}
                  >
                    <Plus className="size-4" />
                    {t("createNewTrip")}
                  </Button>
                )}
            </div>
          </>
        )}
      </div>

      {!isShiftMode && (
        <>
          <Separator />

          <div className="flex shrink-0 justify-end p-4">
            <Button
              variant="solidBlue"
              size="sm"
              onClick={handleConfirm}
              disabled={!canSubmit}
              // size="sm" brings text-xs and cn() does not merge, so the design's 14px is forced.
              className="!text-sm font-medium disabled:!bg-slate-200 disabled:text-slate-400"
            >
              {isSubmitting ? t("transferModalConfirming") : confirmLabel}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

/** "Запланировать новую смену": the autopark's create-shift form for this truck and day. */
const PlanShiftButton: React.FC<{
  truck: ConnectProto.Truck;
  date?: Date;
  activeShifts: { driver_id: number; truck_id: number }[];
  onCreated: (created?: ConnectProto.DriverOnShift) => void;
}> = ({ truck, date, activeShifts, onCreated }) => {
  const tForce = useTypedT(Namespace.ForceCompleteShift);
  const { selectedCompanyId } = useSelectedCompany();
  const drivers = useActiveDriverOptions(selectedCompanyId);

  return (
    <CreateShiftModal
      truckId={String(truck.id)}
      trucks={[truck]}
      drivers={drivers}
      activeShifts={activeShifts}
      defaultDate={date}
      onShiftCreated={onCreated}
      trigger={
        <Button
          variant="opacityBlueWithoutOutline"
          size="sm"
          data-testid="force-complete-shift-plan-shift"
        >
          {tForce("transfer.planShift")}
        </Button>
      }
    />
  );
};

type VehiclePickerProps = {
  vehicles: VehicleGroup[];
  selectedTruckId: bigint | null;
  onSelect: (truckId: bigint) => void;
};

const VehiclePicker: React.FC<VehiclePickerProps> = ({ vehicles, selectedTruckId, onSelect }) => {
  const t = useTypedT(Namespace.Routes);
  const tAny = useT();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selected = vehicles.find((vehicle) => BigInt(vehicle.truck.id) === selectedTruckId);

  const query = normalizeForSearch(search);
  const filtered = query
    ? vehicles.filter((vehicle) => {
        const driverNames = vehicle.shifts
          .map((shift) => formatDriverName(shift, ""))
          .join(" ")
          .toLowerCase();
        return (
          normalizeForSearch(vehicle.truck.plateNumber).includes(query) ||
          normalizeForSearch(vehicle.truck.model).includes(query) ||
          driverNames.includes(query)
        );
      })
    : vehicles;

  return (
    <SearchablePopover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
      trigger={
        <button
          type="button"
          className="flex h-8 min-w-0 !w-96 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 focus:outline-none"
        >
          <IcCar className="size-4 shrink-0 text-blue-600" />
          <span className="min-w-0 flex-1 truncate text-left text-sm text-text-body">
            {selected?.truck.plateNumber ?? t("selectTruck")}
          </span>
          {selected && (
            <span className="shrink-0 text-xs font-medium text-[#667489]">
              {humanTruckType(getTruckTypeValue(selected.truck), tAny)}
            </span>
          )}
          <ChevronDown className="size-4 shrink-0 text-[#667489]" />
        </button>
      }
      searchValue={search}
      onSearchChange={setSearch}
      inputId="route-transfer-truck-search"
      placeholder={t("searchTruckOrDriverPlaceholder")}
      contentClassName="w-96"
      contentWidth="custom"
      listClassName="max-h-60 overflow-y-auto"
    >
      {filtered.map((vehicle) => {
        const truckId = BigInt(vehicle.truck.id);
        const driverName = activeShiftDriverName(vehicle.shifts);
        return (
          <button
            key={String(truckId)}
            type="button"
            onClick={() => {
              onSelect(truckId);
              setSearch("");
              setOpen(false);
            }}
            className={cn(
              "flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm hover:bg-slate-100",
              truckId === selectedTruckId && "bg-blue-100 text-blue-600",
            )}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="font-medium">{vehicle.truck.plateNumber}</span>
            </span>
            {driverName && (
              <span className="min-w-0 truncate text-xs text-[#667489]">{driverName}</span>
            )}
            <span className="shrink-0 text-xs text-[#667489]">
              {humanTruckType(getTruckTypeValue(vehicle.truck), tAny)}
            </span>
          </button>
        );
      })}

      {filtered.length === 0 && (
        <div className="px-4 py-6 text-center text-sm text-[#667489]">{t("trucksNotFound")}</div>
      )}
    </SearchablePopover>
  );
};

type TransferFiltersProps = {
  truckTypeValue: string;
  truckTypeOptions: SearchableSelectOption[];
  onTruckTypeChange: (value: string) => void;
  subOrgValue: string;
  subOrgOptions: SearchableSelectOption[];
  onSubOrgChange: (value: string) => void;
};

const TransferFilters: React.FC<TransferFiltersProps> = ({
  truckTypeValue,
  truckTypeOptions,
  onTruckTypeChange,
  subOrgValue,
  subOrgOptions,
  onSubOrgChange,
}) => {
  const t = useTypedT(Namespace.Routes);
  const [open, setOpen] = React.useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-8 items-center gap-2 rounded-lg border border-slate-200 px-2 text-xs font-medium text-text-body hover:bg-slate-100"
      >
        <IcFilter className="size-4 text-[#667489]" />
        {t("filters")}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-50 mt-2 flex w-[260px] flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
            <SelectField
              inputId="route-transfer-filter-truck-type"
              label={t("truckTypeFilter")}
              value={truckTypeValue}
              options={truckTypeOptions}
              onChange={onTruckTypeChange}
            />
            <SelectField
              inputId="route-transfer-filter-sub-org"
              label={t("subOrganization")}
              value={subOrgValue}
              options={subOrgOptions}
              onChange={onSubOrgChange}
            />
          </div>
        </>
      )}
    </div>
  );
};

type SelectFieldProps = {
  inputId: string;
  label: string;
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
};

const SelectField: React.FC<SelectFieldProps> = ({ inputId, label, value, options, onChange }) => {
  const tAny = useT();

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-body">{label}</span>
      <SearchableSelect
        id={inputId}
        value={value}
        options={options}
        onChange={onChange}
        placeholder={tAny(Namespace.Common, "select")}
        size="sm"
        contentClassName="w-[228px]"
      />
    </div>
  );
};

const FilterChip: React.FC<{ label: string; onRemove: () => void }> = ({ label, onRemove }) => (
  <span className="flex h-6 shrink-0 items-center gap-1 rounded-2xl bg-blue-600 px-2 text-xs font-medium text-white">
    <span className="px-1">{label}</span>
    <button type="button" onClick={onRemove} className="flex items-center" aria-label="remove">
      <X className="size-3.5" />
    </button>
  </span>
);
